import { useAttributeContextFramework } from 'DisplayFramework/hooks/useAttribute';
import { isEmpty } from 'lodash';
import React, { FC, useCallback } from 'react';
import { CompDisplayResponse } from '../../services/displayFramework';
import { DisplayFrameworkContext } from '../context/DisplayFrameworkContext';
import { useDisplayFramework } from '../hooks/useDisplayFramework';

export type AdditionalAttribute = {
  /**
   * Additional Attribute
   */
  entity?: string;
  object?: string;
  entitytype?: string;
  prcode?: string;
  assetfamily?: string;
  assettype?: string;
  assetcategory?: string;
  assetsegment?: string;
  assetmodel?: string;
};

export type DisplayFrameworkProviderProps = {
  /**
   * Page identifier.
   */
  pagcode: string;
  entity?: string;
  entityid?: number;
  additionalAttribute?: AdditionalAttribute;
};

const DisplayFrameworkProvider: FC<DisplayFrameworkProviderProps> = (props) => {
  const { children, pagcode, entity, entityid, additionalAttribute } = props;

  const { dataById, isError, refetch, isFetching } = useDisplayFramework(
    pagcode,
    entity,
    entityid
  );

  const { data } = useAttributeContextFramework(
    additionalAttribute?.entity,
    additionalAttribute?.object,
    additionalAttribute?.entitytype,
    additionalAttribute?.prcode,
    additionalAttribute?.assetfamily,
    additionalAttribute?.assettype,
    additionalAttribute?.assetcategory,
    additionalAttribute?.assetsegment,
    additionalAttribute?.assetmodel
  );

  const getComponentConfiguration = useCallback(
    (comcode: string): CompDisplayResponse | undefined => {
      return dataById[comcode];
    },
    [dataById]
  );

  const getComponentLabel = useCallback(
    (comcode: string): string => {
      const comlabel = dataById[comcode]?.comlabel;
      return !isEmpty(comlabel) ? comlabel : '##' + pagcode + '.' + comcode;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataById]
  );

  const getAttributeListByComcode = useCallback(
    (displaysection: string): any | undefined => {
      return data?.filter(
        (comp: any) => comp.displaysection === displaysection
      );
    },
    [data]
  );

  const getAttributeList = useCallback((): any[] | undefined => {
    return data;
  }, [data]);

  const refresh = () => {
    refetch();
  };

  return (
    <DisplayFrameworkContext.Provider
      value={{
        isFetching,
        isError,
        getComponentConfiguration,
        getComponentLabel,
        pagcode: pagcode,
        refresh,
        getAttributeListByComcode,
        getAttributeList,
      }}
    >
      {children}
    </DisplayFrameworkContext.Provider>
  );
};

export default DisplayFrameworkProvider;
